import {useEffect} from 'react';

import usePopup, {PopupPropsMap, PopupType} from '@/root/hooks/usePopup';
import {useEventPopupStore} from '@/root/stores/eventPopup';

interface UseTemporaryHiddenPopupProps<T extends PopupType> {
  popupProps:
    | (PopupPropsMap[T] & {
        /** 팝업 버전 */
        revision: string;
        /** 다시보지 않기로 미노출시킬 날짜 */
        closeFor: number;
      })
    | null;
  /** 팝업 타입 */
  popupType: T;
  /** 팝업 노출 여부 저장 키 */
  storageKey: string;
  /** 팝업 노출 지연 시간 */
  delay?: number;
  /** 팝업 노출 여부 */
  hidden?: boolean;
}

interface PopupDateStorage {
  [revision: string]: string; // revision을 key로, ISO 날짜 문자열을 value로 저장
}

/**
 * 일정 기간 동안 팝업을 숨기는 기능을 제공하는 훅
 * revision 별로 로컬 스토리지에 날짜를 저장하여 관리
 * popupProps.closeFor 일수만큼 팝업을 표시하지 않음
 */
const useTemporaryHiddenPopup = <T extends PopupType>({
  popupProps,
  popupType,
  storageKey,
  delay = 0,
  hidden = false
}: UseTemporaryHiddenPopupProps<T>) => {
  const {openPopup, closePopup} = usePopup();
  const {isVisible, setIsVisible} = useEventPopupStore();

  useEffect(() => {
    /**
     * 발급 페이지 밑에 홈이 깔려있는 상태에서 서명을 갔다오면 window focus가 발생하여 API 호출
     * 그때는 isTarget이 아직 true라서 팝업이 노출된 상태가 유지됨
     * 따라서 isTarget이 false일 때 노출된 팝업을 닫아야함
     */
    if (hidden || !popupProps || !isVisible) {
      closePopup();
      return;
    }

    const showEventPopup = () => {
      // 로컬 스토리지에서 저장된 데이터 가져오기
      const storedData = localStorage.getItem(storageKey);
      const popupDates: PopupDateStorage = storedData ? JSON.parse(storedData) : {};

      // 현재 revision에 대한 저장된 날짜가 없으면 팝업 표시
      if (!popupDates[popupProps.revision]) {
        openPopup(popupType, popupProps);
        return;
      }

      // 저장된 날짜와 현재 날짜의 차이 계산
      const lastClickTime = new Date(popupDates[popupProps.revision]).getTime();
      const currentTime = new Date().getTime();
      const diffDays = Math.floor((currentTime - lastClickTime) / (1000 * 60 * 60 * 24));

      // closeFor 일수가 지났으면 팝업 표시
      if (diffDays >= (popupProps.closeFor ?? 0)) {
        openPopup(popupType, popupProps);
      }
    };

    const timeoutId = setTimeout(showEventPopup, delay);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      // 새로고침 및 재접속할 때만 팝업이 노출되도록 제어
      setIsVisible(false);
    };
  }, []);
};

/**
 * 특정 revision의 팝업을 다시보지 않기 위해 날짜를 저장하는 함수
 */
export const savePopupDateByRevision = (storageKey: string, revision: string) => {
  const storedData = localStorage.getItem(storageKey);
  const popupDates: PopupDateStorage = storedData ? JSON.parse(storedData) : {};

  // 현재 revision에 대한 날짜 저장
  popupDates[revision] = new Date().toISOString();

  localStorage.setItem(storageKey, JSON.stringify(popupDates));
};

export default useTemporaryHiddenPopup;
